import { useIntl } from 'react-intl'
import { useContext, useMemo } from 'react'
import styled from 'styled-components'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import useDefaultAirport from 'hooks/useDefaultAirport'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { isDepositAvailableOnOffer, isDepositEnabledForOfferType } from 'lib/payment/depositsUtils'
import CurrencyContext from 'contexts/currencyContext'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPrice'
import { unique } from 'lib/array/arrayUtils'

const Pricing = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.desktop} {
    align-items: center;
  }
`

interface Props {
  offer: App.Offer | App.OfferSummary
}

function FrontPageTileFlightOnlyPricing(props: Props) {
  const intl = useIntl()
  const { offer } = props
  const defaultAirport = useDefaultAirport()
  const currentCurrency = useContext(CurrencyContext)

  const flightPrice = defaultAirport?.code ? offer.flightPrices[defaultAirport.code] : undefined

  const prices = useOfferPackagePrice(offer.lowestPricePackage!, offer, { flightPrice })

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  if (!flightPrice) {
    return (
      <Pricing>
        <CaptionTextBlock variant="medium" weight="bold" startIcon={<LinePlaneFlyIcon />}>
          {intl.formatMessage({
            id: 'XvJ6kJwM9sPdlfwvqvg_',
            defaultMessage: 'Hotel + Flights packages',
            description: 'Label indicating hotel and flights bundled packages are available',
          })}
        </CaptionTextBlock>
      </Pricing>
    )
  }

  const totalPrice = prices.totalPriceWithFlights || 0
  const totalValue = prices.totalValue + (prices.flightPrice || 0)
  const showValue = offer.lowestPricePackage?.shouldDisplayValue && totalValue > totalPrice

  const showSecureWithDepositTag = isDepositEnabledForOfferType(offer.type) &&
    isDepositAvailableOnOffer(offer, currentCurrency, undefined, (offer.lowestPricePackage?.price ?? 0))

  return (
    <Pricing>
      { showSecureWithDepositTag && <SecureWithDepositTagAndTooltip offerType={offer.type} /> }
      <CaptionTextBlock variant="medium" weight="bold" startIcon={<LinePlaneFlyIcon />}>
        {intl.formatMessage({
          id: 'ctx10ietnVjW58RdJGh8',
          defaultMessage: 'Hotel + Flights',
          description: 'Label indicating this is a hotel and flights bundle price',
        })}
      </CaptionTextBlock>
      <PriceRowPriceCaption>{intl.formatMessage({
        id: '7rvT1xcehktopf0tHhvE',
        defaultMessage: '{duration} from',
        description: 'Duration label followed by from for flight-only pricing',
      }, { duration: offer.tileDurationLabel })}
      </PriceRowPriceCaption>
      <PriceRowPrice
        data-testid="search-price"
        size="L"
        price={Math.ceil(totalPrice)}
        saleUnit="package"
      />
      <PriceRowCaption>{intl.formatMessage({
        id: 'lPR0ZnPKGA-d_hrcKoqU',
        defaultMessage: '2 adults',
        description: 'Caption indicating the flight package price is for 2 adults',
      })}
      </PriceRowCaption>
      {showValue && <PriceRowValueDiscountWithCaption
        size="M"
        originalValue={Math.ceil(totalValue)}
      />}
      <PriceRowAgentHubCommission
        productType={offer.productType}
        offerId={offer.id}
        offerLocations={offerLocations}
        vendorName={vendorName}
      />
    </Pricing>
  )
}

export default FrontPageTileFlightOnlyPricing
